"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Clock, FileText, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqItems = [{
  id: "item-1",
  question: "What happens immediately after I place an emergency call?",
  answer: "When you call 855-435-6631, you speak straight with a certified restoration supervisor—not an outsourced answering service. We record your location in Maryland, DC, or Northern Virginia, give you immediate steps to safely stop water flow, and dispatch an on-call crew equipped with extraction equipment. Our technicians typically arrive on site within 60 to 90 minutes."
}, {
  id: "item-2",
  question: "How does TriServe coordinate documentation and estimates directly with my insurance company?",
  answer: "Our team completes full infrared thermal imaging and moisture meter mapping on arrival. We document every damaged area with date-stamped photos and build line-item estimates using standard industry software (Xactimate) that adjusters require. We submit this documentation directly to your carrier and work with your adjuster through every phase to protect your claim coverage."
}, {
  id: "item-3",
  question: "How long does the structural drying and dehumidification process typically take?",
  answer: "Most residential and light commercial water losses dry out within 3 to 5 days under continuous commercial airflow and desiccant dehumidification. Daily moisture readings on baseplates, subflooring, and wall cavities track progress until all materials reach their dry standard before any reconstruction begins."
}, {
  id: "item-4",
  question: "Are necessary plumbing source repairs and waterproofing included in the scope?",
  answer: "Yes. Our licensed crews identify and repair the physical source—whether a burst pipe, failed supply line, sewage backflow, or sump pump breakdown—before drying starts. As a full-service general restoration contractor, we also handle sump pump installations, foundation waterproofing, and drywall rebuild under one point of contact."
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0.01 : 0.32,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="faqs" data-nav="light" className="bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar */}
        <div className="mb-10 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <div className="flex items-center gap-2">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-primary" />
            </span>
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Technical Response Guidelines
            </span>
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            24/7 RAPID DISPATCH • MD / DC / NOVA
          </div>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Heading, Supporting Text, and Urgent Support Card */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={transitionConfig} className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                Frequently asked questions
              </span>
              <h2 className="mt-3 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Clear answers before our crews arrive.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Water damage is chaotic. We keep our process transparent from the first minute you call so you know exactly what is happening to your property and your insurance claim.
              </p>
            </div>

            {/* Direct Dispatch Support Card */}
            <div className="mt-8 rounded-lg border border-border bg-muted/50 p-6">
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="font-default text-base font-semibold text-foreground">
                    Have an active water leak right now?
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Do not wait for standard business hours. Our regional crews are on standby 24 hours a day across Maryland, DC, and Northern Virginia.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-center">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                  <a href="tel:855-435-6631">
                    <PhoneCall className="mr-2 size-4" />
                    Call 855-435-6631
                  </a>
                </Button>

                <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted">
                  <Link to="/ContactRequestServicePage">
                    Request inspection
                    <ArrowRight className="ml-1.5 size-4" />
                  </Link>
                </Button>
              </div>

              <div className="mt-4 flex items-center gap-4 border-t border-border/70 pt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Clock className="size-3.5 text-primary" />
                  Average dispatch: under 90 mins
                </span>
                <span className="flex items-center gap-1.5">
                  <FileText className="size-3.5 text-primary" />
                  Direct insurance billing
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Interactive Accordion */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.08
        }} className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-card p-4 sm:p-8 shadow-xs">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full divide-y divide-border">
                {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                    <AccordionItem value={item.id} className="border-none py-2 first:pt-0 last:pb-0">
                      <AccordionTrigger className="text-left font-default text-base font-semibold text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4 pt-1">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </div>

            {/* Micro reassurance strip */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 px-2 text-xs text-muted-foreground">
              <span>No call center transfers — talk straight to field technicians</span>
              <span className="font-mono font-medium text-foreground">
                IICRC Certified Water Restoration (WRT / ASD)
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}